import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const TYPE_OPTIONS = [
  { value: "option1", label: "Option1" },
  { value: "option2", label: "Option2" },
  { value: "option3", label: "Option3" },
] as const;

type TypeValue = (typeof TYPE_OPTIONS)[number]["value"];

interface OptionalDetailRow {
  id: number;
  type: TypeValue | "";
  description: string;
}

type Props = {
  formik: any;
  disabled?: boolean;
};

export default function OptionalOrderDetails({
  formik,
  disabled = false,
}: Props) {
  const [rows, setRows] = useState<OptionalDetailRow[]>([
    { id: 1, type: "", description: "" },
  ]);
  const [initialized, setInitialized] = useState(false);

  const syncRowsToFormik = (currentRows: OptionalDetailRow[]) => {
    const next: Record<string, string> = {
      option1: "",
      option2: "",
      option3: "",
    };
    currentRows.forEach((row) => {
      if (row.type && row.type in next) {
        next[row.type] = row.description;
      }
    });
    formik.setFieldValue("option1", next.option1);
    formik.setFieldValue("option2", next.option2);
    formik.setFieldValue("option3", next.option3);
  };

  useEffect(() => {
    if (!initialized) return;
    syncRowsToFormik(rows);
  }, [rows, initialized]); // eslint-disable-line

  useEffect(() => {
    if (initialized) return;

    const v = formik.values;
    const initialRows: OptionalDetailRow[] = [];
    TYPE_OPTIONS.forEach((opt, index) => {
      const val = v?.[opt.value];
      if (val != null && String(val).trim() !== "") {
        initialRows.push({
          id: index + 1,
          type: opt.value,
          description: String(val),
        });
      }
    });

    if (initialRows.length > 0) {
      setRows(initialRows);
    }
    setInitialized(true);
  }, [formik.values, initialized]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: Date.now(), type: "", description: "" },
    ]);
  };

  const deleteRow = (id: number) => {
    if (rows.length <= 1) {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, type: "", description: "" } : r))
      );
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const setRowType = (id: number, type: TypeValue | "") => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, type } : r))
    );
  };

  const setRowDescription = (id: number, description: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, description } : r))
    );
  };

  const usedTypes = new Set(
    rows.filter((r) => r.type).map((r) => r.type)
  ) as Set<TypeValue | "">;
  const allTypesUsed = usedTypes.size === TYPE_OPTIONS.length;

  const getOptionsForRow = (row: OptionalDetailRow) =>
    TYPE_OPTIONS.filter(
      (opt) => opt.value === row.type || !usedTypes.has(opt.value)
    );

  return (
    <Box>
      <Typography variant="h5" sx={{  mb: 2 }}>
        Optional Order Details
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            pb: 1,
            mb: 0.5,
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="subtitle2" sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", fontWeight: 600 }}>
              Type
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", fontWeight: 600 }}>
              Description
            </Typography>
          </Box>
          <Box sx={{ width: 48, flexShrink: 0 }} />
        </Box>
        {rows.map((row) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ flex: "0 0 200px" }}>
              <FormControl fullWidth size="small">
                <InputLabel id={`type-label-${row.id}`}>Type</InputLabel>
                <Select
                  labelId={`type-label-${row.id}`}
                  value={row.type}
                  label="Type"
                  onChange={(e) =>
                    setRowType(row.id, (e.target.value || "") as TypeValue | "")
                  }
                  disabled={disabled || !formik.values.clientId}
                  displayEmpty
                  renderValue={(v) =>
                    v ? TYPE_OPTIONS.find((o) => o.value === v)?.label ?? "" : "Type"
                  }
                >
                  {getOptionsForRow(row).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={1}
                maxRows={4}
                placeholder="Description"
                value={row.description}
                onChange={(e) => setRowDescription(row.id, e.target.value)}
                disabled={disabled || !formik.values.clientId}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, height: 40 }}>
              <Button
                size="small"
                onClick={() => deleteRow(row.id)}
                disabled={disabled || !formik.values.clientId}
                sx={{ minWidth: "auto", p: 0.5, color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        ))}
        {!allTypesUsed && (
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={addRow}
            sx={{
              color: "primary.main",
              textTransform: "none",
              p: 0,
              alignSelf: "flex-start",
            }}
            disabled={disabled || !formik.values.clientId}
          >
            ADD OPTIONAL ORDER DETAILS
          </Button>
        )}
      </Box>
    </Box>
  );
}
